// The portal's only chrome, the site's: an ink bar that says whose office
// this is and holds the way out, a paper strip with whose case, and the
// places a client can go. No nav rail, no firm tooling.

import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FolderOpen, House } from "lucide-react";
import { fetchClientCase, getClientToken } from "../../api/clientPortal";
import { usePortalSignOut } from "./portalSession";
import { useScreens } from "../chrome/screens";
import { FirmMark } from "./FirmMark";

// The site's glyphs for these tabs, at its stroke.
const TABS = [
  { to: "/portal", label: "Your case", icon: <House strokeWidth={1.9} aria-hidden /> },
  {
    to: "/portal/documents",
    label: "Documents",
    icon: <FolderOpen strokeWidth={1.9} aria-hidden />,
  },
];

export function PortalHeader() {
  const location = useLocation();
  const signOut = usePortalSignOut();
  const token = getClientToken();
  const tabs = [...TABS, ...useScreens().flatMap((s) => s.portalTabs ?? [])];

  // Same key the case screen uses, so this is a cache read on the way in and
  // one small request on a cold link straight to the documents page.
  const { data, isPending } = useQuery({
    queryKey: ["portal", "case", token],
    queryFn: fetchClientCase,
    retry: false,
  });

  return (
    <header className="portal-top">
      <div className="portal-bar">
        {/* Blank until the answer, as the doors' head is: a lockup that
            changes under the client's eyes is worse than a moment of nothing. */}
        <div className="portal-brand">
          {!isPending && (
            <>
              <FirmMark firmName={data?.firmName} logoVersion={data?.logoVersion} size={30} />
              <span className="portal-firm">{data?.firmName ?? "DuraLex"}</span>
            </>
          )}
        </div>
        {/* Beside the name rather than in the strip: it is an action, not a
            section, which is what the strip is named for. */}
        <button className="portal-signout" onClick={signOut}>
          Sign out
        </button>
      </div>
      <div className="portal-who">
        <div className="portal-client">{isPending ? "" : (data?.clientName ?? "Your case")}</div>
        <div className="portal-ref">
          {data?.eId && (
            <>
              Case <span className="mono">{data.eId}</span>
            </>
          )}
        </div>
      </div>
      {/* One strip: a row under the name at a desk, the bar at the foot of
          a phone (portal.css). */}
      <nav className="portal-nav" aria-label="Portal sections">
        {tabs.map((t) => {
          const here = location.pathname === t.to;
          return (
            <Link
              key={t.to}
              to={t.to}
              className={"portal-nav-link" + (here ? " active" : "")}
              aria-current={here ? "page" : undefined}
            >
              {t.icon}
              <span>{t.label}</span>
            </Link>
          );
        })}
      </nav>
    </header>
  );
}
